import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Icon,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  alpha,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Card,
  CardContent,
  Alert,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { menuItemsService } from "../../../services/menu-items.service";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { posSellableItemsService } from "../../../services/pos-sellable-items.service";
import { inventoryService } from "../../../services/inventory.service";
import { AllergiesSelector } from "../../../components/AllergiesSelector/AllergiesSelector";
import { IngredientDialog } from "../../Kitchen/Recipes/components/IngredientDialog";
import { IngredientOption, ExtendedBatch } from "../../Kitchen/Recipes/types";
import { MenuItem as MenuItemService } from "../../../services/menu-items.service";
import { ModifierGroup } from "../../../../pos/types/pos.types";
import { ModifierEditor } from "../../../components/ModifierEditor/ModifierEditor";

interface BatchInfo {
  batches: ExtendedBatch[];
  totalCost: number;
  averageCost: number;
  totalQuantity: number;
  lastFetched: number;
}

interface SellableItemEntry {
  pos_sellable_item_id: string;
  name: string;
  quantity: string;
  total_cost: number;
}

interface IngredientEntry {
  _id: string;
  name: string;
  quantity: string;
  ingredient_id: string;
  unit: string;
  notes?: string;
  batch_id?: string;
  brand_name: string;
  item_id: string;
  item_type: string;
}

interface FormIngredient extends IngredientEntry {
  _id: string;
  name: string;
  quantity: string;
  ingredient_id: string;
  unit: string;
  notes: string;
  batch_id: string;
  brand_name: string;
  item_id: string;
  item_type: "ingredient";
}

interface FormData {
  name: string;
  description: string;
  category: string;
  price: number;
  total_cost: number;
  sellable_items: SellableItemEntry[];
  allergies: string[];
  expected_prep_duration: string;
  branch_id?: string;
  is_available: boolean;
  modifiers: ModifierGroup[];
}

const initialFormData: FormData = {
  name: "",
  description: "",
  category: "",
  price: 0,
  total_cost: 0,
  sellable_items: [],
  allergies: [],
  expected_prep_duration: "",
  is_available: true,
  modifiers: [],
};

const categories = [
  { value: "main_course", label: "Main Course" },
  { value: "appetizer", label: "Appetizer" },
  { value: "dessert", label: "Dessert" },
  { value: "beverage", label: "Beverage" },
];

export const THEME_COLORS = {
  primary: "#0891B2", // Cyan-600
  primaryHover: "#0E7490", // Cyan-700
  secondary: "#64748B", // Slate-500
  danger: "#DC2626", // Red-600
  dangerHover: "#B91C1C", // Red-700
  success: "#059669", // Emerald-600
  background: {
    main: "#FFFFFF",
    light: "#F8FAFC",
    card: "#FFFFFF",
  },
  border: {
    light: alpha("#0891B2", 0.1),
    main: alpha("#0891B2", 0.2),
  },
  text: {
    primary: "#1E293B", // Slate-800
    secondary: "#64748B", // Slate-500
    light: "#94A3B8", // Slate-400
  },
};

export const MenuItemForm: React.FC = () => {
  console.log("MenuItemForm mounted");
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [sellableItemDialogOpen, setSellableItemDialogOpen] = useState(false);
  const [availableSellableItems, setAvailableSellableItems] = useState<any[]>(
    []
  );
  const [selectedSellableItem, setSelectedSellableItem] = useState<any>(null);
  const [sellableItemQuantity, setSellableItemQuantity] = useState("");
  const [loadingSellableItems, setLoadingSellableItems] = useState(false);

  const [modifiers, setModifiers] = React.useState<ModifierGroup[]>([]);

  useEffect(() => {
    console.log("useEffect running, id:", id);
    if (id) {
      loadMenuItem();
    }
    loadSellableItems();
  }, [id]);

  const loadMenuItem = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await menuItemsService.getMenuItem(id);
      if (response.success && response.data) {
        const menuItem = response.data;
        // Map sellable items to use pos_sellable_item_id instead of _id
        const mappedSellableItems = menuItem.sellable_items.map((item) => ({
          pos_sellable_item_id: item._id,
          name: item.name,
          quantity: item.quantity,
          total_cost: 0, // This will be updated when we fetch the actual cost
        }));

        setFormData({
          name: menuItem.name,
          description: menuItem.description || "",
          category: menuItem.category || "",
          price: menuItem.price,
          total_cost: menuItem.total_cost,
          sellable_items: mappedSellableItems,
          allergies: menuItem.allergies || [],
          expected_prep_duration: menuItem.expected_prep_duration || "",
          branch_id: menuItem.branch_id,
          is_available: menuItem.is_available,
          modifiers: menuItem.modifiers || [],
        });
        setModifiers(menuItem.modifiers || []);

        // Fetch total costs for each sellable item
        const updatedSellableItems = await Promise.all(
          mappedSellableItems.map(async (item) => {
            try {
              const sellableItemResponse =
                await posSellableItemsService.getPOSSellableItem(
                  item.pos_sellable_item_id
                );
              const totalCost =
                (sellableItemResponse.data as any).data?.total_cost || 0;
              return {
                ...item,
                total_cost: totalCost,
              };
            } catch (error) {
              console.error(
                `Error fetching sellable item ${item.pos_sellable_item_id}:`,
                error
              );
              return item;
            }
          })
        );

        setFormData((prev) => ({
          ...prev,
          sellable_items: updatedSellableItems,
        }));
      }
    } catch (error) {
      console.error("Error loading menu item:", error);
      toast.error("Failed to load menu item");
    } finally {
      setLoading(false);
    }
  };

  const loadSellableItems = async () => {
    setLoadingSellableItems(true);
    try {
      const response = await posSellableItemsService.listPOSSellableItems({
        searchQuery: "",
        sortField: "created_at",
        sortOrder: "asc",
        pageIndex: 0,
        pageSize: 25,
      });
      if (response.data && Array.isArray(response.data)) {
        const mappedItems = response.data.map(
          (item: { _id: string; name: string; total_cost: number }) => ({
            pos_sellable_item_id: item._id,
            name: item.name,
            total_cost: item.total_cost,
          })
        );
        setAvailableSellableItems(mappedItems);
      }
    } catch (error) {
      console.error("Error loading sellable items:", error);
      toast.error("Failed to load sellable items");
    } finally {
      setLoadingSellableItems(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleAddSellableItem = async () => {
    if (!selectedSellableItem || !sellableItemQuantity) return;

    try {
      // Fetch the sellable item's total cost
      const sellableItemResponse =
        await posSellableItemsService.getPOSSellableItem(
          selectedSellableItem.pos_sellable_item_id
        );
      // The response has a nested structure: { success: true, data: { total_cost: number, ... } }
      const sellableItemTotalCost = (sellableItemResponse.data as any).data
        ?.total_cost;
      console.log("Sellable item response:", sellableItemResponse.data); // Debug log
      console.log("Sellable item total cost:", sellableItemTotalCost); // Debug log

      const newItem: SellableItemEntry = {
        pos_sellable_item_id: selectedSellableItem.pos_sellable_item_id,
        name: selectedSellableItem.name,
        quantity: sellableItemQuantity,
        total_cost: sellableItemTotalCost || 0, // Ensure we have a default value
      };

      setFormData((prev) => {
        const updatedSellableItems = [...prev.sellable_items, newItem];
        const newTotalCost = updatedSellableItems.reduce((sum, item) => {
          const itemQuantity = parseFloat(item.quantity) || 0;
          const itemCost = item.total_cost || 0;
          console.log(
            `Item ${item.name}: quantity=${itemQuantity}, cost=${itemCost}`
          ); // Debug log
          return sum + itemCost * itemQuantity;
        }, 0);
        console.log("New total cost:", newTotalCost); // Debug log

        return {
          ...prev,
          sellable_items: updatedSellableItems,
          total_cost: newTotalCost,
        };
      });

      setSelectedSellableItem(null);
      setSellableItemQuantity("");
      setSellableItemDialogOpen(false);
    } catch (error) {
      console.error("Error adding sellable item:", error);
      toast.error("Failed to add sellable item");
    }
  };

  const handleRemoveSellableItem = (itemId: string) => {
    setFormData((prev) => {
      const updatedSellableItems = prev.sellable_items.filter(
        (item) => item.pos_sellable_item_id !== itemId
      );
      const newTotalCost = updatedSellableItems.reduce((sum, item) => {
        const itemQuantity = parseFloat(item.quantity) || 0;
        const itemCost = item.total_cost || 0;
        return sum + itemCost * itemQuantity;
      }, 0);

      return {
        ...prev,
        sellable_items: updatedSellableItems,
        total_cost: newTotalCost,
      };
    });
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.category) {
      newErrors.category = "Category is required";
    }
    if (formData.price <= 0) {
      newErrors.price = "Price must be greater than 0";
    }
    if (formData.sellable_items.length === 0) {
      newErrors.sellable_items = "At least one sellable item is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Validate required fields
      const errors: Record<string, string> = {};
      if (!formData.name) errors.name = "Name is required";
      if (!formData.category) errors.category = "Category is required";
      if (!formData.price) errors.price = "Price is required";
      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        return;
      }

      // Prepare sellable items data with _id instead of pos_sellable_item_id
      const sellableItemsData = formData.sellable_items.map(
        ({ pos_sellable_item_id, name, quantity }) => ({
          _id: pos_sellable_item_id,
          name,
          quantity,
        })
      );

      const menuItemData = {
        ...formData,
        sellable_items: sellableItemsData,
        modifiers,
      };

      if (id) {
        // Update existing menu item
        await menuItemsService.updateMenuItem({
          _id: id,
          ...menuItemData,
        });
        toast.success("Menu item updated successfully");
      } else {
        // Create new menu item
        await menuItemsService.createMenuItem(menuItemData);
        toast.success("Menu item created successfully");
      }

      navigate("/business/menu/menu-items");
    } catch (error) {
      console.error("Error submitting menu item:", error);
      toast.error("Failed to save menu item");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/business/menu/menu-items");
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <PageHeader
        title={id ? "Edit Menu Item" : "Create Menu Item"}
        subtitle="Manage your menu items and their details"
        icon="restaurant_menu"
        backButton
        onBack={handleCancel}
      />

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${alpha(THEME_COLORS.primary, 0.1)}`,
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ mx: "auto", mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ color: THEME_COLORS.primary }}>description</Icon>
              Basic Information
            </Typography>

            <Card
              sx={{
                border: `1px solid ${THEME_COLORS.border.light}`,
                borderRadius: 2,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      error={!!errors.name}
                      helperText={
                        errors.name ||
                        "Enter a descriptive name for your menu item"
                      }
                      required
                      placeholder="e.g., Grilled Chicken Sandwich"
                      InputProps={{
                        startAdornment: (
                          <Icon sx={{ mr: 1, color: THEME_COLORS.primary }}>
                            restaurant_menu
                          </Icon>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: THEME_COLORS.primary,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: THEME_COLORS.primary,
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.category} required>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={formData.category}
                        label="Category"
                        onChange={(e) =>
                          handleInputChange("category", e.target.value)
                        }
                        startAdornment={
                          <Icon sx={{ mr: 1, color: THEME_COLORS.primary }}>
                            category
                          </Icon>
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: THEME_COLORS.primary,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: THEME_COLORS.primary,
                            },
                          },
                        }}
                      >
                        {categories.map((category) => (
                          <MenuItem key={category.value} value={category.value}>
                            {category.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.category ? (
                        <FormHelperText error>{errors.category}</FormHelperText>
                      ) : (
                        <FormHelperText>
                          Select the category this item belongs to
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={4}
                      value={formData.description}
                      onChange={(e) =>
                        handleInputChange("description", e.target.value)
                      }
                      placeholder="Describe your menu item in detail, including its main features and special preparation notes..."
                      helperText="A detailed description helps staff understand how to prepare and serve the item"
                      InputProps={{
                        startAdornment: (
                          <Icon
                            sx={{ mr: 1, mt: 1, color: THEME_COLORS.primary }}
                          >
                            description
                          </Icon>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: THEME_COLORS.primary,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: THEME_COLORS.primary,
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ color: THEME_COLORS.primary }}>inventory_2</Icon>
              Menu Item Components
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icon sx={{ color: THEME_COLORS.primary }}>
                            inventory_2
                          </Icon>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                              color: THEME_COLORS.text.primary,
                              fontFamily: "Montserrat",
                              fontWeight: 600,
                            }}
                          >
                            Sellable Items
                            {availableSellableItems.length === 0 ? (
                              <div>No items loaded</div>
                            ) : (
                              <div>
                                {availableSellableItems.length} items loaded
                              </div>
                            )}
                          </Box>
                        </Stack>
                        <Button
                          startIcon={<Icon>add</Icon>}
                          onClick={() => setSellableItemDialogOpen(true)}
                          variant="contained"
                          size="small"
                          sx={{
                            bgcolor: THEME_COLORS.primary,
                            "&:hover": {
                              bgcolor: THEME_COLORS.primaryHover,
                            },
                          }}
                        >
                          Add Item
                        </Button>
                      </Stack>
                      <List
                        sx={{ bgcolor: "background.paper", borderRadius: 1 }}
                      >
                        {formData.sellable_items.length > 0 ? (
                          formData.sellable_items.map((item) => (
                            <ListItem
                              key={item.pos_sellable_item_id}
                              sx={{
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                "&:last-child": { borderBottom: "none" },
                              }}
                            >
                              <ListItemText
                                primary={item.name}
                                secondary={`Quantity: ${item.quantity}`}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  onClick={() =>
                                    handleRemoveSellableItem(
                                      item.pos_sellable_item_id
                                    )
                                  }
                                  size="small"
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    bgcolor: alpha(THEME_COLORS.danger, 0.08),
                                    color: THEME_COLORS.danger,
                                    backdropFilter: "blur(8px)",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                      transform: "translateY(-1px)",
                                      bgcolor: alpha(THEME_COLORS.danger, 0.12),
                                      boxShadow:
                                        "0 4px 8px rgba(220, 38, 38, 0.15)",
                                    },
                                    "&:active": {
                                      transform: "translateY(0)",
                                    },
                                  }}
                                >
                                  <Icon sx={{ fontSize: 18 }}>
                                    delete_outlined
                                  </Icon>
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))
                        ) : (
                          <Alert severity="info" sx={{ m: 1 }}>
                            No sellable items added yet
                          </Alert>
                        )}
                      </List>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ color: THEME_COLORS.primary }}>settings</Icon>
              Additional Information
            </Typography>

            <Card
              sx={{
                border: `1px solid ${THEME_COLORS.border.light}`,
                borderRadius: 2,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Price"
                      type="number"
                      value={formData.price}
                      onChange={(e) =>
                        handleInputChange("price", parseFloat(e.target.value))
                      }
                      error={!!errors.price}
                      helperText={errors.price || "Enter the selling price"}
                      required
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <Icon sx={{ mr: 1, color: THEME_COLORS.primary }}>
                            attach_money
                          </Icon>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: THEME_COLORS.primary,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: THEME_COLORS.primary,
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Total Cost"
                      type="number"
                      value={formData.total_cost.toFixed(2)}
                      InputProps={{
                        startAdornment: (
                          <Icon sx={{ mr: 1, color: THEME_COLORS.primary }}>
                            payments
                          </Icon>
                        ),
                        readOnly: true,
                      }}
                      helperText="Total cost is automatically calculated from sellable items"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: THEME_COLORS.primary,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: THEME_COLORS.primary,
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Preparation Time"
                      value={formData.expected_prep_duration}
                      onChange={(e) =>
                        handleInputChange(
                          "expected_prep_duration",
                          e.target.value
                        )
                      }
                      placeholder="e.g., 15-20 minutes"
                      helperText="Estimated time to prepare this item"
                      InputProps={{
                        startAdornment: (
                          <Icon sx={{ mr: 1, color: THEME_COLORS.primary }}>
                            timer
                          </Icon>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: THEME_COLORS.primary,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: THEME_COLORS.primary,
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        bgcolor: alpha(THEME_COLORS.primary, 0.04),
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Icon sx={{ color: THEME_COLORS.primary }}>
                          warning
                        </Icon>
                        Allergen Information
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Select all allergens present in this menu item to ensure
                        proper labeling and customer safety
                      </Typography>
                      <AllergiesSelector
                        selectedAllergies={formData.allergies}
                        onChange={(allergies) =>
                          handleInputChange("allergies", allergies)
                        }
                        defaultExpanded={true}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ color: THEME_COLORS.primary }}>inventory_2</Icon>
              Modifiers
            </Typography>
            <ModifierEditor modifiers={modifiers} onChange={setModifiers} />
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleCancel}
              startIcon={<Icon>close</Icon>}
              sx={{
                borderColor: THEME_COLORS.border.main,
                color: THEME_COLORS.primary,
                "&:hover": {
                  borderColor: THEME_COLORS.primary,
                  bgcolor: alpha(THEME_COLORS.primary, 0.04),
                },
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={saving}
              startIcon={<Icon>save</Icon>}
              sx={{
                bgcolor: THEME_COLORS.primary,
                "&:hover": {
                  bgcolor: THEME_COLORS.primaryHover,
                },
              }}
            >
              {id ? "Update" : "Create"} Menu Item
            </LoadingButton>
          </Stack>
        </form>
      </Paper>

      {/* Enhanced Sellable Items Dialog */}
      <Dialog
        open={sellableItemDialogOpen}
        onClose={() => setSellableItemDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon sx={{ color: THEME_COLORS.primary }}>inventory_2</Icon>
            Add Sellable Item
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Sellable Item</InputLabel>
                  <Select
                    value={selectedSellableItem?.pos_sellable_item_id || ""}
                    label="Sellable Item"
                    onChange={(e) => {
                      const item = availableSellableItems.find(
                        (i) => i.pos_sellable_item_id === e.target.value
                      );
                      setSelectedSellableItem(item || null);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: THEME_COLORS.primary,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: THEME_COLORS.primary,
                        },
                      },
                    }}
                  >
                    {availableSellableItems.map((item) => (
                      <MenuItem
                        key={item.pos_sellable_item_id}
                        value={item.pos_sellable_item_id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select an item from your inventory
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Quantity"
                  value={sellableItemQuantity}
                  onChange={(e) => setSellableItemQuantity(e.target.value)}
                  placeholder="e.g., 2 pieces"
                  helperText="Specify the quantity needed for this menu item"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: THEME_COLORS.primary,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: THEME_COLORS.primary,
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setSellableItemDialogOpen(false)}
            startIcon={<Icon>close</Icon>}
            sx={{
              borderColor: THEME_COLORS.border.main,
              color: THEME_COLORS.primary,
              "&:hover": {
                borderColor: THEME_COLORS.primary,
                bgcolor: alpha(THEME_COLORS.primary, 0.04),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSellableItem}
            disabled={!selectedSellableItem || !sellableItemQuantity}
            startIcon={<Icon>add</Icon>}
            sx={{
              bgcolor: THEME_COLORS.primary,
              "&:hover": {
                bgcolor: THEME_COLORS.primaryHover,
              },
            }}
          >
            Add Item
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
